import { PropsWithChildren, useCallback, useMemo, useState } from "react";

import { TrashIcon } from "@radix-ui/react-icons";
import {
  Box,
  Button,
  CheckboxGroup,
  Flex,
  Grid,
  Heading,
  IconButton,
  Separator,
  Text,
  TextField,
  Tooltip,
} from "@radix-ui/themes";
import { createColumnHelper, Row } from "@tanstack/react-table";
import { useForm, UseFormReturn } from "react-hook-form";
import { SingleValue } from "react-select";

import { AddressForm } from "./AddressForm";
import {
  ESTIMATED_ANNUAL_USAGE_OPTIONS,
  PROJECT_TYPE_OPTIONS,
  PURCHASING_DECISION_TIMELINE_OPTIONS,
  SUPPORT_REQUESTS_OPTIONS,
} from "./constants";
import {
  PurchasingDecisionTimeline,
  SampleRequest,
  SampleRequestEstimatedAnnualUsage,
  SampleRequestProjectType,
  SampleRequestRawMaterial,
} from "../../api/public";
import { Callout, DateInput, Select, Table } from "../../common/components";
import { RowActions } from "../../common/components/Table/RowActions";
import Textarea from "../../common/components/Textarea";
import { useSampleRequestContext } from "../../state/SampleRequestContext";

const columnHelper = createColumnHelper<SampleRequestRawMaterial>();

const estimatedAnnualUsageToOption = (
  value: SampleRequestEstimatedAnnualUsage
) => {
  return ESTIMATED_ANNUAL_USAGE_OPTIONS.find((v) => v.value === value);
};

const purchasingDecisionToOption = (value: PurchasingDecisionTimeline) => {
  return PURCHASING_DECISION_TIMELINE_OPTIONS.find((v) => v.value === value);
};

const projectTypeToOption = (value: SampleRequestProjectType) => {
  return PROJECT_TYPE_OPTIONS.find((v) => v.value === value);
};

const useSampleRequestFormTableColumns = ({
  form,
  onChange,
  onDeleteRow,
}: {
  form: UseFormReturn<SampleRequest>;
  onChange: (rawMaterial: SampleRequestRawMaterial) => void;
  onDeleteRow: (rows: Row<SampleRequestRawMaterial>[], rowId: string) => void;
}) => {
  const {
    register,
    formState: { errors },
  } = form;
  return useMemo(
    () => [
      columnHelper.accessor("raw_material.trade_name", {
        header: () => <Text size="2">Ingredient</Text>,
        cell: ({ cell }) => {
          return (
            <Flex direction="column">
              <Text size="2">{cell.getValue()}</Text>
              <Text size="2" color="gray">
                by {cell.row.original.raw_material.supplier_name}
              </Text>
            </Flex>
          );
        },
      }),
      columnHelper.accessor("sample_quantity", {
        header: () => <Text size="2">Sample Size</Text>,
        cell: ({ cell }) => {
          return (
            <Flex direction="column" gap="2">
              <TextField.Root
                key={`selected_raw_materials.${cell.row.index}.sample_quantity`}
                {...register(
                  `selected_raw_materials.${cell.row.index}.sample_quantity`,
                  {
                    required: "Please provide a sample quantity",
                  }
                )}
                color={
                  errors.selected_raw_materials?.[cell.row.index]
                    ?.sample_quantity && "red"
                }
                onChange={(e) => {
                  onChange({
                    ...cell.row.original,
                    sample_quantity: e.target.value,
                  });
                }}
                style={{ height: "38px" }}
              />
              {errors.selected_raw_materials?.[cell.row.index]
                ?.sample_quantity && (
                <Text size="2" color="red">
                  {
                    errors.selected_raw_materials?.[cell.row.index]
                      ?.sample_quantity?.message
                  }
                </Text>
              )}
            </Flex>
          );
        },
      }),
      columnHelper.accessor("estimated_annual_usage", {
        header: () => <Text size="2">Estimated Annual Usage</Text>,
        cell: ({ cell }) => {
          const value = cell.getValue();
          return (
            <Flex direction="column" gap="2">
              <Select
                value={value && estimatedAnnualUsageToOption(value)}
                menuPortalTarget={document.body}
                menuPosition="fixed"
                {...register(
                  `selected_raw_materials.${cell.row.index}.estimated_annual_usage`,
                  {
                    required: "Please provide an estimated annual usage",
                  }
                )}
                onChange={(e) => {
                  onChange({
                    ...cell.row.original,
                    estimated_annual_usage: (
                      e as SingleValue<{
                        label: string;
                        value: SampleRequestEstimatedAnnualUsage;
                      }>
                    )?.value,
                  });
                }}
                options={ESTIMATED_ANNUAL_USAGE_OPTIONS}
                isMulti={false}
              />
              {errors.selected_raw_materials?.[cell.row.index]
                ?.estimated_annual_usage && (
                <Text size="2" color="red">
                  {
                    errors.selected_raw_materials?.[cell.row.index]
                      ?.estimated_annual_usage?.message
                  }
                </Text>
              )}
            </Flex>
          );
        },
      }),
      columnHelper.display({
        id: "actions",
        cell: ({ row, table }) => {
          return (
            <RowActions
              visible={table.options.meta?.getHoveredRowIndex?.() === row.index}
            >
              <Tooltip content="Click to delete">
                <IconButton
                  variant="ghost"
                  onClick={() =>
                    onDeleteRow?.(table.getCoreRowModel().rows, row.id)
                  }
                >
                  <TrashIcon />
                </IconButton>
              </Tooltip>
            </RowActions>
          );
        },
        size: 0,
      }),
    ],
    [errors.selected_raw_materials, onChange, onDeleteRow, register]
  );
};

const WithLabelAndError = ({
  label,
  errorMessage,
  children,
}: PropsWithChildren<{
  label: string;
  errorMessage?: string;
}>) => {
  return (
    <Flex gap="2" justify="between">
      <Text size="2" color="gray">
        {label}
      </Text>
      <Box width="50%">
        {children}
        {errorMessage && (
          <Text size="2" color="red">
            {errorMessage}
          </Text>
        )}
      </Box>
    </Flex>
  );
};

type Props = {
  pendingSampleRequest: SampleRequest;
};

export const SampleRequestForm = ({ pendingSampleRequest }: Props) => {
  const [isEditingAddress, setIsEditingAddress] = useState(false);

  const {
    removeFromSampleRequest,
    updateSampleRequest,
    updateSampleRequestRawMaterial,
    submitSampleRequest,
    isSubmittingSampleRequest,
  } = useSampleRequestContext();
  const form = useForm({
    values: pendingSampleRequest,
    mode: "onSubmit",
    shouldFocusError: true,
  });

  const {
    watch,
    register,
    formState: { errors },
  } = form;

  const handleSubmit = form.handleSubmit(() => {
    submitSampleRequest();
  });

  const handleDeleteRow = useCallback(
    (rows: Row<SampleRequestRawMaterial>[], rowId: string) => {
      const row = rows.find((r) => r.id === rowId);
      if (row) {
        removeFromSampleRequest(row.original.raw_material.id);
      }
    },
    [removeFromSampleRequest]
  );

  const selectedRawMaterials = watch("selected_raw_materials");

  const projectType = watch("project_type");

  const purchasingDecisionTimeline = watch("purchasing_decision_timeline");

  const handleRowChange = useCallback(
    (rawMaterial: SampleRequestRawMaterial) => {
      updateSampleRequestRawMaterial({
        estimated_annual_usage: rawMaterial.estimated_annual_usage,
        id: rawMaterial.id,
        raw_material_id: rawMaterial.raw_material.id,
        sample_quantity: rawMaterial.sample_quantity,
      });
    },
    [updateSampleRequestRawMaterial]
  );

  const columns = useSampleRequestFormTableColumns({
    form,
    onDeleteRow: handleDeleteRow,
    onChange: handleRowChange,
  });

  const rows = useMemo(() => {
    return selectedRawMaterials ?? [];
  }, [selectedRawMaterials]);
  const supportRequests = watch("support_requests");
  if (isEditingAddress) {
    return (
      <Flex direction="column" gap="4">
        <Heading size="3">Shipping Information</Heading>
        <AddressForm
          defaultValue={pendingSampleRequest.shipping_details}
          onCancel={() => setIsEditingAddress(false)}
          onSubmit={(shippingDetails) => {
            updateSampleRequest({
              ...watch(),
              shipping_details: shippingDetails,
            });
            setIsEditingAddress(false);
          }}
        />
      </Flex>
    );
  }

  return (
    <Flex direction="column" gap="4">
      <Heading size="3">Raw Material Information</Heading>
      {rows.length === 0 && (
        <Callout color="gray">
          <Text size="2" color="gray">
            Your cart is empty.
          </Text>
        </Callout>
      )}

      {rows.length > 0 && <Table data={rows} columns={columns} />}
      <Separator size="4"></Separator>
      <Heading size="3">Sample Request Information</Heading>
      <WithLabelAndError
        label="Project Description"
        errorMessage={errors.project_description?.message}
      >
        <Textarea
          placeholder={"Describe your project"}
          {...register("project_description", {
            required: "Please provide a project description",
            validate: (value) => {
              if (value.length === 0) {
                return "Please provide a project description";
              }
            },
          })}
          onChange={(e) => {
            updateSampleRequest({
              ...watch(),
              project_description: e.currentTarget.value,
            });
          }}
        />
      </WithLabelAndError>
      <WithLabelAndError label="Needed By Date">
        <DateInput
          value={watch("needed_by_date")?.split("T")[0]}
          onChange={(e) => {
            updateSampleRequest({
              ...watch(),
              needed_by_date: e.currentTarget.value + "T00:00:00+00:00",
            });
          }}
        />
      </WithLabelAndError>
      <WithLabelAndError
        label="Support Requests"
        errorMessage={errors.support_requests?.message}
      >
        <CheckboxGroup.Root
          orientation="horizontal"
          defaultValue={supportRequests}
        >
          <Flex gap="3" wrap="wrap">
            {SUPPORT_REQUESTS_OPTIONS.map((option) => {
              return (
                <CheckboxGroup.Item
                  value={option.value}
                  onClick={() => {
                    if (supportRequests.includes(option.value)) {
                      updateSampleRequest({
                        ...watch(),
                        support_requests: supportRequests.filter(
                          (v) => v !== option.value
                        ),
                      });
                    } else {
                      updateSampleRequest({
                        ...watch(),
                        support_requests: [...supportRequests, option.value],
                      });
                    }
                  }}
                >
                  {option.label}
                </CheckboxGroup.Item>
              );
            })}
          </Flex>
        </CheckboxGroup.Root>
      </WithLabelAndError>
      <WithLabelAndError label="Project Type">
        <Select
          menuPlacement="auto"
          placeholder={"Select project type..."}
          options={PROJECT_TYPE_OPTIONS}
          isMulti={false}
          {...register("project_type", {
            required: "Please select a project type",
          })}
          value={projectType && projectTypeToOption(projectType)}
          onChange={(e) => {
            updateSampleRequest({
              ...watch(),
              project_type: (
                e as SingleValue<{
                  label: string;
                  value: SampleRequestProjectType;
                }>
              )?.value,
            });
          }}
        />
      </WithLabelAndError>
      <WithLabelAndError label="Purchasing Decision Timeline">
        <Select
          menuPlacement="auto"
          placeholder={"Select purchasing decision timeline..."}
          options={PURCHASING_DECISION_TIMELINE_OPTIONS}
          isMulti={false}
          {...register("purchasing_decision_timeline", {
            required: "Please select a decision timeline",
          })}
          value={
            purchasingDecisionTimeline &&
            purchasingDecisionToOption(purchasingDecisionTimeline)
          }
          onChange={(e) => {
            updateSampleRequest({
              ...watch(),
              purchasing_decision_timeline: (
                e as SingleValue<{
                  label: string;
                  value: PurchasingDecisionTimeline;
                }>
              )?.value,
            });
          }}
        />
      </WithLabelAndError>
      <WithLabelAndError label="Additional Notes">
        <Textarea
          placeholder={"Additional notes"}
          {...register("additional_notes")}
          onChange={(e) => {
            updateSampleRequest({
              ...watch(),
              additional_notes: e.currentTarget.value,
            });
          }}
        />
      </WithLabelAndError>
      <Flex justify={"between"}>
        <Heading size="3">Shipping Information</Heading>
        <Button
          size="1"
          variant="surface"
          onClick={() => setIsEditingAddress(true)}
        >
          Edit
        </Button>
      </Flex>
      <Flex direction="column" gap="2">
        <WithLabelAndError label="Address">
          <Text size="2">
            {pendingSampleRequest.shipping_details.address_line_1 +
              (pendingSampleRequest.shipping_details.address_line_2?.length
                ? `, ${pendingSampleRequest.shipping_details.address_line_2}`
                : "")}
          </Text>
        </WithLabelAndError>
        <WithLabelAndError label="City">
          <Text size="2">{pendingSampleRequest.shipping_details.city}</Text>
        </WithLabelAndError>
        <WithLabelAndError label="State">
          <Text size="2">{pendingSampleRequest.shipping_details.state}</Text>
        </WithLabelAndError>
        <WithLabelAndError label="Zip">
          <Text size="2">{pendingSampleRequest.shipping_details.zip}</Text>
        </WithLabelAndError>
        <WithLabelAndError label="Country">
          <Text size="2">{pendingSampleRequest.shipping_details.country}</Text>
        </WithLabelAndError>
        <WithLabelAndError label="FedEx #">
          <Text size="2">
            {pendingSampleRequest.shipping_details.fedex_account_number ?? "-"}
          </Text>
        </WithLabelAndError>
        <WithLabelAndError label="UPS #">
          <Text size="2">
            {pendingSampleRequest.shipping_details.ups_account_number ?? "-"}
          </Text>
        </WithLabelAndError>
        <WithLabelAndError label="DHL #">
          <Text size="2">
            {pendingSampleRequest.shipping_details.dhl_account_number ?? "-"}
          </Text>
        </WithLabelAndError>
      </Flex>
      <Button
        onClick={handleSubmit}
        disabled={isSubmittingSampleRequest || rows.length === 0}
      >
        Submit
      </Button>
    </Flex>
  );
};
